import { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Heading,
  Container,
  Stack,
  Input,
  Textarea,
  Button,
  CheckboxGroup,
  Fieldset,
  SimpleGrid,
  HStack,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useController } from 'react-hook-form';
import { z } from 'zod';
import { useParams, useNavigate } from 'react-router-dom';
import { toaster } from '@/components/ui/toaster';
import { Field } from '@/components/ui/field';
import { Checkbox } from '@/components/ui/checkbox';
import WorkspaceLayout from '@/pages/workspace/workspace-layout';
import useCollectionTypesStore from '@/utils/use-collection-type';
import useContentTypesStore from '@/utils/use-content-type';
import { ContentType, CollectionContentType } from '@/lib/types';
import api from '@/utils/api';

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  libraries: z.string().array(),
});

type FormData = z.infer<typeof formSchema>;

export default function EditCollectionType() {
  const [collectionContentTypes, setCollectionContentTypes] = useState<CollectionContentType[]>([]);
  const [loadingItems, setLoadingItems] = useState<Map<number, boolean>>(new Map());
  const [previousSelectedIds, setPreviousSelectedIds] = useState<string[]>([]);

  const navigate = useNavigate();
  const { loadCollectionTypes } = useCollectionTypesStore();
  const { contentTypes } = useContentTypesStore();
  const { workspaceId, id } = useParams<{
    workspaceId: string;
    id: string;
  }>();

  const {
    control,
    handleSubmit,
    register,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  const library = useController({
    control,
    name: 'libraries',
    defaultValue: [],
  });

  const selectedLibraries = watch('libraries');

  const getCollectionTypes = useCallback(async () => {
    if (!id) return;

    try {
      const [collectionTypeRes, collectionLibraryRes] = await Promise.all([
        api.get(`collection-types/${id}`),
        api.get(`collection-content-types/collection-types/${id}`),
      ]);

      setCollectionContentTypes(collectionLibraryRes.data || []);

      if (collectionTypeRes.data) {
        const collectionType = collectionTypeRes.data;
        const ids = collectionLibraryRes.data.map((library: any) =>
          library.content_type_id.toString(),
        );

        reset({
          name: collectionType.name,
          description: collectionType.description,
          libraries: ids,
        });

        setPreviousSelectedIds(ids);
      }
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to fetch collection content library',
      });
    }
  }, [id, reset]);

  useEffect(() => {
    getCollectionTypes();
  }, [getCollectionTypes]);

  const onSubmit = async (data: FormData) => {
    if (!workspaceId || !id) {
      return;
    }

    try {
      await api.put(`collection-types/${id}`, {
        name: data.name,
        description: data.description,
        workspace_id: parseInt(workspaceId),
      });

      const existingIds = collectionContentTypes.map((type) => type.content_type_id);
      const uniqueIds = data.libraries.filter(
        (libraryId) => !existingIds.includes(parseInt(libraryId, 10)),
      );

      if (uniqueIds.length > 0) {
        await Promise.all(
          uniqueIds.map((libraryId) =>
            api.post(`collection-content-types`, {
              workspace_id: parseInt(workspaceId, 10),
              content_type_id: parseInt(libraryId, 10),
              collection_type_id: parseInt(id, 10),
            }),
          ),
        );
      }

      if (workspaceId) {
        loadCollectionTypes(workspaceId);
      }

      toaster.create({
        type: 'success',
        title: 'Collection updated successfully',
        duration: 3000,
      });

      navigate(`/${workspaceId}/collection-types`);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to update content collection',
      });
    }
  };

  const handleDeleteLibrary = useCallback(
    async (contentType: ContentType) => {
      if (loadingItems.get(contentType.id)) return;

      setLoadingItems((prev) => new Map(prev).set(contentType.id, true));

      try {
        const existingType = collectionContentTypes.find(
          (type) => type.content_type_id === contentType.id,
        );

        if (existingType) {
          await api.delete(`collection-content-types/${existingType.id}`);

          setCollectionContentTypes((prev) => prev.filter((type) => type.id !== existingType.id));

          getCollectionTypes();

          toaster.create({
            title: `"${contentType.name}" library was removed from collection`,
            type: 'success',
            duration: 2000,
          });
        }
      } catch (err: any) {
        toaster.create({
          type: 'error',
          title: err.response?.data?.error || 'Failed to delete collection content library',
        });

        library.field.onChange([...selectedLibraries, contentType.id.toString()]);
      } finally {
        setLoadingItems((prev) => {
          const next = new Map(prev);
          next.delete(contentType.id);
          return next;
        });
      }
    },
    [loadingItems, collectionContentTypes, getCollectionTypes, library.field, selectedLibraries],
  );

  useEffect(() => {
    if (!selectedLibraries || !previousSelectedIds.length) return;

    const removedIds = previousSelectedIds.filter((id) => !selectedLibraries.includes(id));

    removedIds.forEach((removedId) => {
      const contentType = contentTypes.find((type) => type.id.toString() === removedId);
      if (contentType) {
        handleDeleteLibrary(contentType);
      }
    });

    setPreviousSelectedIds(selectedLibraries);
  }, [selectedLibraries, contentTypes, previousSelectedIds, handleDeleteLibrary]);

  return (
    <WorkspaceLayout>
      <Card.Root minH={'100vh'} mx={'auto'} width={{ md: '90%' }} maxW={'6xl'}>
        <Card.Header>
          <Container maxW={'3xl'}>
            <Heading size='3xl'>Edit custom collection</Heading>
          </Container>
        </Card.Header>
        <Card.Body>
          <Container maxW={'3xl'}>
            <form id='edit-collection-type' onSubmit={handleSubmit(onSubmit)}>
              <Stack align={'stretch'} gap={4}>
                <Field label='Name' invalid={!!errors.name} errorText={errors.name?.message}>
                  <Input {...register('name')} />
                </Field>
                <Field label='Description'>
                  <Textarea {...register('description')} rows={4} />
                </Field>

                {contentTypes.length > 0 && (
                  <Fieldset.Root invalid={!!errors.libraries}>
                    <Fieldset.Legend>
                      Choose the content libraries you want to use when adding content to this
                      collection:
                    </Fieldset.Legend>

                    <CheckboxGroup
                      invalid={!!errors.libraries}
                      value={library.field.value}
                      onValueChange={library.field.onChange}
                      name={library.field.name}
                      colorPalette='brand'
                    >
                      <SimpleGrid columns={{ base: 2, md: 3 }} gap={4} pl={4}>
                        {contentTypes.map((item) => (
                          <Checkbox
                            key={item.id}
                            value={item.id.toString()}
                            disabled={loadingItems.get(item.id) || false}
                          >
                            <HStack>
                              <Text>{item.name}</Text>
                              {loadingItems.get(item.id) && <Spinner size='sm' />}
                            </HStack>
                          </Checkbox>
                        ))}
                      </SimpleGrid>
                    </CheckboxGroup>

                    {errors.libraries && (
                      <Fieldset.ErrorText>{errors.libraries.message}</Fieldset.ErrorText>
                    )}
                  </Fieldset.Root>
                )}

                <Button
                  colorPalette='brand'
                  form='edit-collection-type'
                  type='submit'
                  width='full'
                  mt={5}
                  loading={isSubmitting}
                  loadingText='Loading...'
                >
                  Save
                </Button>
              </Stack>
            </form>
          </Container>
        </Card.Body>
      </Card.Root>
    </WorkspaceLayout>
  );
}
